/**
 * CompositeScoringReport — /#/reports/composite-scoring
 *
 * Per-deal view of composite score, the rep's declared timing %, the
 * displayed (blended) forecast %, and the RULE-FORECAST-1 category floor.
 * Surfaces soft-stop flags and the manager-attention flag at 50 %.
 */
import { useEffect, useMemo, useState } from "react";
import ReportShell, { thStyle, tdStyle, BLACK, RED, SLATE, BORDER, EmptyState, LoadingState } from "./_ReportShell";
import {
  CompositeScoringRow,
  ForecastCategory,
  downloadCsv,
  listCompositeScoringRows,
  toCsv,
} from "@/lib/reports-saturday";

// Canon colors per CANON.md lines 167-186.
function categoryColor(c: ForecastCategory): string {
  switch (c) {
    case "Commit":    return "#16A34A"; // green
    case "Upside":    return "#16A34A"; // green
    case "Best Case": return "#EAB308"; // yellow
    case "Pipeline":  return "#EA580C"; // orange
    case "Omit":      return "#DC2626"; // red
  }
}

export default function CompositeScoringReport() {
  const [rows, setRows] = useState<CompositeScoringRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await listCompositeScoringRows();
        if (!cancelled) setRows(r);
      } catch (err: any) {
        console.error("[CompositeScoringReport]", err);
        if (!cancelled) setError(err?.message ?? "Failed to load");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const csv = useMemo(() => {
    if (!rows) return "";
    return toCsv(
      rows.map((r) => ({
        deal_id: r.deal_id,
        account_name: r.account_name ?? "",
        ae_name: r.ae_name ?? "",
        composite_pct: r.composite_pct ?? "",
        declared_timing: r.declared_timing ?? "",
        declared_timing_pct: r.declared_timing_pct ?? "",
        displayed_forecast_pct: r.displayed_forecast_pct,
        rule_forecast_1_category: r.rule_forecast_1_category,
        manager_attention: r.manager_attention ? "yes" : "",
        soft_stop_flags: r.soft_stop_flags.join("|"),
        close_date: r.close_date ?? "",
      })),
    );
  }, [rows]);

  return (
    <ReportShell
      title="Composite Scoring"
      subtitle="Per-deal composite score, declared timing, blended forecast, and RULE-FORECAST-1 category."
      rightLabel="Composite Scoring"
      onExport={rows && rows.length > 0 ? () => downloadCsv("composite-scoring.csv", csv) : undefined}
    >
      {error ? (
        <EmptyState message={`Could not load: ${error}`} />
      ) : !rows ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState message="No timing records yet. Data activates as reps complete CP10 timing checkpoints." />
      ) : (
        <div style={{ overflowX: "auto", border: `1px solid ${BORDER}`, borderRadius: 8, background: "#FFFFFF" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
            <thead>
              <tr>
                <th style={thStyle}>Account</th>
                <th style={thStyle}>AE</th>
                <th style={thStyle}>Composite %</th>
                <th style={thStyle}>Declared Timing</th>
                <th style={thStyle}>Displayed Forecast %</th>
                <th style={thStyle}>RULE-FORECAST-1</th>
                <th style={thStyle}>Soft-stop flags</th>
                <th style={thStyle}>Close date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.deal_id}>
                  <td style={tdStyle}>{r.account_name ?? "—"}</td>
                  <td style={tdStyle}>{r.ae_name ?? "—"}</td>
                  <td style={tdStyle}>{r.composite_pct != null ? `${r.composite_pct}%` : "—"}</td>
                  <td style={tdStyle}>
                    {r.declared_timing_pct != null ? (r.declared_timing === "below_50" ? "Below 50%" : `${r.declared_timing_pct}%`) : "—"}
                    {r.manager_attention && (
                      <span style={{ marginLeft: 8, padding: "2px 6px", borderRadius: 4, background: "#FEF3C7", color: "#92400E", fontSize: 10, fontWeight: 700 }}>
                        MANAGER ATTENTION
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>{r.displayed_forecast_pct}%</td>
                  <td style={tdStyle}>
                    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, background: `${categoryColor(r.rule_forecast_1_category)}22`, color: categoryColor(r.rule_forecast_1_category), fontSize: 11, fontWeight: 700 }}>
                      {r.rule_forecast_1_category}
                    </span>
                    <div style={{ fontSize: 11, color: SLATE, marginTop: 4 }}>{r.category_eligibility_notes}</div>
                  </td>
                  <td style={tdStyle}>
                    {r.soft_stop_flags.length === 0
                      ? <span style={{ color: SLATE }}>—</span>
                      : r.soft_stop_flags.map((f) => (
                          <div key={f} style={{ fontSize: 11, color: RED }}>{f}</div>
                        ))}
                  </td>
                  <td style={tdStyle}>{r.close_date ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ReportShell>
  );
}
